import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  IconButton,
  Select,
  MenuItem,
  TextField,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { updateField, removeField, reorderFields } from '../../redux/queryBuilder/queryBuilderSlice';
import {
  getTableOptions,
  getFieldsForTable,
  getOperatorsForDataType,
  getFieldMetadata,
  TABLE_DEFINITIONS,
  OPERATORS
} from './utils/schemaDefinitions';
import DragHandle from './DragHandle';

const BOOLEAN_OPTIONS = [
  { label: 'Yes', value: '1' },
  { label: 'No', value: '0' }
];

const SortableRow = ({ field, index, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f5f5f5' : 'transparent'
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ width: 60, textAlign: 'center', py: 1 }}>
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          isDragging={isDragging}
        />
      </TableCell>
      {children}
    </TableRow>
  );
};

const QueryFieldsGrid = ({ selectedFields }) => {
  const dispatch = useDispatch();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = selectedFields.findIndex(f => f.id === active.id);
      const newIndex = selectedFields.findIndex(f => f.id === over.id);

      dispatch(reorderFields({ oldIndex, newIndex }));
    }
  };

  const handleTableChange = (fieldId, tableName) => {
    const tableDefinition = TABLE_DEFINITIONS[tableName];
    dispatch(
      updateField({
        id: fieldId,
        updates: {
          tableName,
          tableFriendlyName: tableDefinition?.friendlyName || '',
          fieldName: '',
          fieldFriendlyName: '',
          dataType: 'string',
          operator: 'equals',
          criteria: ''
        }
      })
    );
  };

  const handleFieldChange = (fieldId, fieldName, tableName) => {
    const fieldMetadata = getFieldMetadata(tableName, fieldName);
    if (fieldMetadata) {
      dispatch(
        updateField({
          id: fieldId,
          updates: {
            fieldName,
            fieldFriendlyName: fieldMetadata.friendlyName,
            dataType: fieldMetadata.dataType,
            operator: 'equals'
          }
        })
      );
    }
  };

  const handleOperatorChange = (fieldId, operator) => {
    dispatch(
      updateField({
        id: fieldId,
        updates: { operator }
      })
    );
  };

  const handleCriteriaChange = (fieldId, criteria) => {
    dispatch(
      updateField({
        id: fieldId,
        updates: { criteria }
      })
    );
  };

  const handleRemoveField = (fieldId) => {
    dispatch(removeField(fieldId));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ width: 60, fontWeight: 'bold' }}></TableCell>
              <TableCell sx={{ width: 80, fontWeight: 'bold' }}>Actions</TableCell>
              <TableCell sx={{ minWidth: 180, fontWeight: 'bold' }}>Table</TableCell>
              <TableCell sx={{ minWidth: 180, fontWeight: 'bold' }}>Field</TableCell>
              <TableCell sx={{ minWidth: 180, fontWeight: 'bold' }}>Operator</TableCell>
              <TableCell sx={{ minWidth: 200, fontWeight: 'bold' }}>Criteria</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <SortableContext
              items={selectedFields.map(f => f.id)}
              strategy={verticalListSortingStrategy}
            >
              {selectedFields.map((field, index) => (
                <SortableRow key={field.id} field={field} index={index}>
                  <TableCell sx={{ py: 1 }}>
                    <IconButton
                      onClick={() => handleRemoveField(field.id)}
                      size='small'
                      color='error'
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size='small'>
                      <Select
                        value={field.tableName || ''}
                        onChange={(e) => handleTableChange(field.id, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value=''>
                          <em>Select Table...</em>
                        </MenuItem>
                        {getTableOptions().map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size='small'>
                      <Select
                        value={field.fieldName || ''}
                        onChange={(e) =>
                          handleFieldChange(field.id, e.target.value, field.tableName)
                        }
                        disabled={!field.tableName}
                        displayEmpty
                      >
                        <MenuItem value=''>
                          <em>Select Field...</em>
                        </MenuItem>
                        {getFieldsForTable(field.tableName).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    <FormControl fullWidth size='small'>
                      <Select
                        value={field.operator || 'equals'}
                        onChange={(e) => handleOperatorChange(field.id, e.target.value)}
                        disabled={!field.fieldName}
                      >
                        {getOperatorsForDataType(field.dataType).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell sx={{ py: 1 }}>
                    {(() => {
                      const operatorDef = OPERATORS.find(op => op.value === field.operator);
                      const requiresCriteria = operatorDef?.requiresCriteria !== false;

                      return field.dataType === 'boolean' ? (
                        // Boolean field - render dropdown
                        <FormControl fullWidth size='small'>
                          <Select
                            value={field.criteria || ''}
                            onChange={(e) => handleCriteriaChange(field.id, e.target.value)}
                            disabled={!field.fieldName || !requiresCriteria}
                            displayEmpty
                          >
                            <MenuItem value='' disabled>
                              <em>Select value...</em>
                            </MenuItem>
                            {BOOLEAN_OPTIONS.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        // All other field types - render text field
                        <TextField
                          fullWidth
                          size='small'
                          value={field.criteria || ''}
                          onChange={(e) => handleCriteriaChange(field.id, e.target.value)}
                          disabled={!field.fieldName || !requiresCriteria}
                          placeholder={
                            field.operator === 'between'
                              ? 'value1, value2'
                              : field.operator === 'in'
                              ? 'value1, value2, value3'
                              : 'Enter value...'
                          }
                          type={
                            field.dataType === 'int' || field.dataType === 'decimal'
                              ? 'text'
                              : field.dataType === 'datetime'
                              ? 'date'
                              : 'text'
                          }
                        />
                      );
                    })()}
                  </TableCell>
                </SortableRow>
              ))}
            </SortableContext>
          </TableBody>
        </Table>
      </TableContainer>
    </DndContext>
  );
};

export default QueryFieldsGrid;
